import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Receipt, CreditCard, Banknote } from 'lucide-react';
import api from '@/lib/api';
import { formatDateFromIso } from '@/lib/datetime';

export default function CustomerPayments() {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await api.get('/customer/payments');
                setPayments(res.data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar los pagos.');
            } finally { setLoading(false); }
        };
        fetch();
    }, []);

    if (loading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    if (error) {
        return (
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Mis Pagos</h1>
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-12 text-center">
                        <Receipt className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const methodIcon = (method) => {
        if (method === 'cash') return <Banknote className="h-4 w-4" />;
        if (method === 'mercadopago') return <CreditCard className="h-4 w-4" />;
        return <Receipt className="h-4 w-4" />;
    };

    const methodLabel = (method) => {
        if (method === 'cash') return 'Efectivo';
        if (method === 'mercadopago') return 'MercadoPago';
        return method;
    };

    const statusBadge = (status) => {
        const variants = {
            completed: 'bg-white text-black',
            pending: 'bg-white/10 text-white',
            failed: 'bg-red-500/20 text-red-400',
            refunded: 'bg-white/10 text-white/60',
        };
        const labels = {
            completed: 'Completado',
            pending: 'Pendiente',
            failed: 'Fallido',
            refunded: 'Reembolsado',
        };
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${variants[status] || variants.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Mis Pagos</h1>

            {payments.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-12 text-center">
                        <Receipt className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">No tenés pagos registrados.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {payments.map(p => (
                        <Card key={p.id} className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                        {methodIcon(p.method)}
                                    </div>
                                    <div>
                                        <CardTitle className="text-sm font-semibold uppercase tracking-wide">{methodLabel(p.method)}</CardTitle>
                                        <p className="text-xs text-white/60">{p.gym?.name || 'Gimnasio'}</p>
                                    </div>
                                </div>
                                {statusBadge(p.status)}
                            </CardHeader>
                            <CardContent className="pt-0">
                                <div className="flex items-center justify-between">
                                    <span className="text-2xl font-bold">${Number(p.amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                                    <span className="text-xs text-white/60">
                                        {formatDateFromIso(p.paid_at || p.created_at)}
                                    </span>
                                </div>
                                {p.notes && <p className="text-xs text-white/50 mt-2">{p.notes}</p>}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}